import { Component, Inject, isDevMode, OnDestroy, OnInit } from '@angular/core';
import {
  AzureLogout,
  GetExternalApiData,
  InitSso,
  RemoveNotifications,
  SetSelectedRoute,
  GetBannerData,
  SetUser,
} from './store/api-eco.actions';
import { Store } from '@ngxs/store';
import { AuthData, Notification } from './store';
import { ApiEcoState } from './store/api-eco.state';
import { UserState } from './store/user-store/user.state';
import { SetSelectedRouteFromRouteState } from './store/route-store/route.actions';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { NavigationEnd, Router } from '@angular/router';
import { DOCUMENT, Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { TitleService } from './services/title.service';
import { ProgressSpinnerDialogService } from './services/progress-spinner-dialog.service';
import { ContextStatusType, ContextUpdateType, UserContextService } from './services/user-context.service';
import { HostListener } from '@angular/core';
import { APIELogService } from './services/apie-log.service';
import { AlertService } from './services/alert.service';
import { DataService } from './services/data.service';

@Component({
  selector: 'kp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  timedOut = false;
  lastPing: Date | null = null;
  grantType = 'authorization_code';
  authData?: AuthData;
  subscription?: Subscription;
  authExpires?: Observable<any>;
  signal = new Subject();
  selectedRoute = 'home';
  outletHeight = 0;
  isIframe = false;
  b2cPolicies: any;
  applicationErrorSubstring = 'application with this name already exists';
  applicationErrorTitle = 'Duplicate Application Name';

  private readonly destroyed = new Subject();
  private readonly allowedIdleMinutes = 15; // User will be logged out after 15 minutes of inactivity
  private idleCountdownTimer: any; // Timer for console countdown
  private elapsedIdleSeconds = 0; // Track elapsed idle time
  private lastActivityTime: number = Date.now(); // Track last user activity time
  private tokenRefreshTimer: any; // Timer for token refresh every 30 minutes
  private refreshCountdownTimer: any; // Timer for console countdown for token refresh
  private elapsedRefreshSeconds = 0; // Track elapsed time since last token refresh
  private idleInitialized = false; // Track if idle watcher has been initialized

  /**
   * Detect user activity (clicks, keyboard, etc) to reset idle timer
   * Only resets timer for logged-in users
   */
  @HostListener('document:click')
  @HostListener('document:keydown')
  @HostListener('document:mousemove')
  onUserActivity(): void {
    // Only track activity for logged-in users
    const context = this.userContextService.getContext();
    if (context.session?.status !== ContextStatusType.LOGGED_IN) {
      return;
    }

    const currentTime = Date.now();
    // Only reset if at least 1 second has passed since last activity to avoid excessive resets
    if (currentTime - this.lastActivityTime > 1000) {
      this.lastActivityTime = currentTime;
      this.resetIdle();
    }
  }

  /**
   *
   * @param store
   * @param toasterService
   * @param idle
   * @param keepalive
   * @param router
   * @param location
   * @param titleService
   * @param document
   * @param progressDialogService
   * @param userContextService
   * @param logService
   */
  constructor(
    private readonly store: Store,
    private readonly idle: Idle,
    private readonly keepalive: Keepalive,
    private readonly router: Router,
    private readonly location: Location,
    private readonly titleService: TitleService,
    @Inject(DOCUMENT) public document: Document,
    private readonly progressDialogService: ProgressSpinnerDialogService,
    private readonly userContextService: UserContextService,
    private readonly logService: APIELogService,
    private readonly alertService: AlertService,
    private readonly dataService: DataService
  ) {
    this.b2cPolicies = environment.b2cPolicies;
  }

  /**
   *
   */
  ngOnInit(): void {
    const context = this.userContextService.getContext();
    // tokenExpiry is stored in seconds (Unix epoch). Compare directly against Date.now()/1000.
    // Do NOT multiply by 1000 — that was causing the infinite reload loop.
    // Navigate to home instead of reloading so we don't re-enter this check immediately.
    if (context.global?.tokenExpiry && Math.floor(Date.now() / 1000) >= Number(context.global.tokenExpiry)) {
      localStorage.clear();
      sessionStorage.clear();
      this.router.navigate(['/home']);
    }
    this.userContextService.initService(this.destroyed);

    this.getExternalApiData();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroyed))
      .subscribe((event: NavigationEnd) => {
        const route = event.urlAfterRedirects.substr(event.urlAfterRedirects.indexOf('/') + 1);
        this.selectedRoute = route;
        this.store.dispatch(new SetSelectedRoute(route));
        this.store.dispatch(new SetSelectedRouteFromRouteState(route));

        window.scrollTo(0, 0);
        this.outletHeight = window.innerHeight - 85;
        this.progressDialogService.hideSpinner();
      });

    this.store
      .select(ApiEcoState.getSelectedRoute)
      .pipe(takeUntil(this.destroyed))
      .subscribe((route: string) => {
        if (route && route !== this.selectedRoute) {
          this.router.navigate([route]);
        }
      });

    // Fetch banner data for all users (logged in or out)
    setTimeout(() => {
      this.store.dispatch(new GetBannerData());
    }, 1000);

    // Subscribe to persisted UserState for rehydration on reload
    this.store
      .select(UserState.getUserFromUserState)
      .pipe(takeUntil(this.destroyed))
      .subscribe(user => {
        // Fall back to NGXS-persisted userToken when global_context.token is missing
        // (e.g. on first load of a new tab before MSAL broadcast fires)
        const contextToken = this.userContextService.getTokenInfo()?.token;
        const hasUserToken = Boolean(contextToken || user?.userToken);
        const apiEcoUser = this.store.selectSnapshot(ApiEcoState.getUser);

        if (user?.isAuthenticated && hasUserToken) {
          if (!apiEcoUser?.isAuthenticated) {
            this.store.dispatch(new SetUser({ ...user }));
          }

          // Initialize idle watcher only once
          if (!this.idleInitialized) {
            this.initIdleWatcher();
          }
        } else if (user?.isAuthenticated && !hasUserToken) {
          this.logService.log(
            'Skipping protected bootstrap calls because user token is missing from context',
            'warning',
            'APP INIT -> TOKEN MISSING'
          );
        } else {
          // Clear timers and reset flag if user logs out
          this.idleInitialized = false;
          this.clearIdleCountdownTimer();
          this.clearTokenRefreshTimer();
        }
      });

    this.subscribeToNotifications();
  }

  /**
   *
   * @param event Catches unload event when refreshing in a logged-in state.
   * @returns
   */
  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    const context = this.userContextService.getContext();
    this.logService.log('Browser Refresh occurring..', 'error', 'USER_CONTEXT -> BROWSER REFRESH');
    if (context?.session?.status === 'loggedIn') {
      this.userContextService.updateContext(ContextUpdateType.STATUS, ContextStatusType.LOGGED_IN_PENDING_REFRESH);
    }
    return true;
  }

  /**
   *
   */
  onBackClicked(): void {
    this.location.back();
  }

  /**
   *
   * @returns
   */
  showBackButton(): boolean {
    const showOnRoutes = ['login', 'reset-password', 'contact', 'api-subscribe', 'app-details'];
    return showOnRoutes.some(route => this.selectedRoute?.startsWith(route));
  }

  /**
   *
   */
  subscribeToNotifications(): void {
    this.store
      .select(ApiEcoState.getNotifications)
      .pipe(takeUntil(this.destroyed))
      .subscribe((notifications: Notification[]) => {
        if (notifications?.length) {
          notifications.forEach((notification: Notification) => {
            let options;
            if (typeof notification.duration === 'number' && notification.duration >= 0) {
              options = notification.duration > 0 ? { timeOut: notification.duration } : { disableTimeOut: true };
            }
            if (notification.error) {
              this.alertService.showErrorAlert(notification, options);
            } else if (notification.warning) {
              this.alertService.showWarningAlert(notification, options);
            } else {
              this.alertService.showSuccessAlert(notification, options);
            }
          });
          this.alertService.setAttributeForToastrForScreenReaders();
          this.store.dispatch(new RemoveNotifications(notifications));
        }
      });
  }

  /**
   * Initialize idle watcher for session timeout management.
   * Logs out user after 15 minutes of continuous inactivity.
   * Refreshes token every 25 minutes regardless of user activity.
   * Timer resets on user activity (clicks, scrolls, etc).
   */
  initIdleWatcher(): void {
    // Prevent duplicate initialization
    if (this.idleInitialized) {
      return;
    }
    this.idleInitialized = true;

    // Set idle timeout to 15 minutes (900 seconds) - timeout point for logout
    this.idle.setIdle(this.allowedIdleMinutes * 60);

    // Set logout timeout to 1 second after idle (total 901 seconds)
    this.idle.setTimeout(1);

    // Set default interrupts (clicks, scrolls, touches, key presses)
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // When user becomes active again, reset the idle state
    this.idle.onIdleEnd
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        this.resetIdle();
      });

    // When idle timeout is reached (at 15 minutes) - log out without warning
    this.idle.onTimeout
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        // const timeoutMsg = '%c⏹️  IDLE TIMEOUT TRIGGERED (15 minutes) - LOGGING OUT';
        // const timeoutStyle = 'color: #FF0000; font-weight: bold; font-size: 14px;';
        // console.log(timeoutMsg, timeoutStyle);
        this.timedOut = true;
        this.logService.log('Session timeout - user logged out due to 15 minutes of inactivity', 'error');
        // Stop the countdown timers first
        this.clearIdleCountdownTimer();
        this.clearTokenRefreshTimer();

        // Clear storage immediately
        // const clearMsg = '%c🗑️  Clearing localStorage and sessionStorage';
        // const clearStyle = 'color: #FF6600; font-weight: bold;';
        // console.log(clearMsg, clearStyle);
        localStorage.clear();
        sessionStorage.clear();

        // Dispatch logout actions
        this.store.dispatch(new AzureLogout({} as any));
        this.userContextService.logout();

        // Reload page after a short delay to ensure actions are processed
        setTimeout(() => {
          // const reloadMsg = '%c🔄 Redirecting to Login';
          // const reloadStyle = 'color: #0066FF; font-weight: bold;';
          // console.log(reloadMsg, reloadStyle);
          // window.location.href = '/login';
          window.location.reload();
        }, 500);
      });

    // Set keepalive ping interval to 15 seconds to maintain backend session
    this.keepalive.interval(15);

    // Track last ping time for monitoring
    this.keepalive.onPing
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        this.lastPing = new Date();
        this.logService.log(`Session keepalive ping at ${this.lastPing}`, 'info');
      });

    // Start watching for idle state
    this.idle.watch();
    // const watchMsg =
    //   '%c👁️  Idle watcher started - monitoring for 25 minute inactivity, token refresh every 5 minutes';
    // const watchStyle = 'color: #2196F3; font-weight: bold;';
    // console.log(watchMsg, watchStyle);

    // Start console countdown timer
    this.startIdleCountdownTimer();

    // Start token refresh timer (every 25 minutes)
    this.startTokenRefreshTimer();
  }

  /**
   *
   */
  resetIdle(): void {
    this.idle.watch();
    this.timedOut = false;
    this.clearIdleCountdownTimer();
    this.startIdleCountdownTimer();
    // const successMsg = '%c✅ Session Activity Detected - Idle Timer Reset';
    // const successStyle = 'color: #4CAF50; font-weight: bold; font-size: 12px;';
    // console.log(successMsg, successStyle);
  }

  /**
   * Starts a console countdown timer that displays idle time every second.
   * Shows current elapsed time and remaining time before logout (15 minutes).
   */
  private startIdleCountdownTimer(): void {
    this.clearIdleCountdownTimer();
    this.elapsedIdleSeconds = 0;

    // Log that timer is starting
    // const startMsg = '%c✅ SESSION IDLE TIMER STARTED - 15 minute timeout, no warning';
    // const startStyle = 'color: #2196F3; font-weight: bold; font-size: 12px;';
    // console.log(startMsg, startStyle);

    this.idleCountdownTimer = setInterval(() => {
      this.elapsedIdleSeconds++;
      // const elapsedMinutes = Math.floor(this.elapsedIdleSeconds / 60);
      // const elapsedSeconds = this.elapsedIdleSeconds % 60;
      // const totalSeconds = this.allowedIdleMinutes * 60;
      // const remainingSeconds = Math.max(0, totalSeconds - this.elapsedIdleSeconds);
      // const remainingMinutes = Math.floor(remainingSeconds / 60);
      // const remainingSecs = remainingSeconds % 60;

      // const timeStr = `${elapsedMinutes}:${String(elapsedSeconds).padStart(2, '0')} / ${remainingMinutes}:${String(remainingSecs).padStart(2, '0')}`;
      // console.log(`%c⏱️  Session Idle Time: ${timeStr}`, 'color: #4CAF50; font-weight: bold;');
    }, 1000);
  }

  /**
   * Clears the idle countdown timer.
   */
  private clearIdleCountdownTimer(): void {
    if (this.idleCountdownTimer) {
      clearInterval(this.idleCountdownTimer);
      this.idleCountdownTimer = null;
    }
    this.elapsedIdleSeconds = 0;
  }

  /**
   * Starts a console countdown timer for token refresh.
   * Displays every second showing elapsed time and remaining time until next token refresh.
   */
  private startRefreshCountdownTimer(): void {
    this.clearRefreshCountdownTimer();
    this.elapsedRefreshSeconds = 0;

    // Log that timer is starting
    // const startMsg = '%c✅ TOKEN REFRESH COUNTDOWN STARTED - 25 minute refresh interval';
    // const startStyle = 'color: #FF9800; font-weight: bold; font-size: 12px;';
    // console.log(startMsg, startStyle);

    this.refreshCountdownTimer = setInterval(() => {
      this.elapsedRefreshSeconds++;
      // const elapsedMinutes = Math.floor(this.elapsedRefreshSeconds / 60);
      // const elapsedSeconds = this.elapsedRefreshSeconds % 60;
      // const totalSeconds = 25 * 60; // 25 minutes in seconds
      // const remainingSeconds = Math.max(0, totalSeconds - this.elapsedRefreshSeconds);
      // const remainingMinutes = Math.floor(remainingSeconds / 60);
      // const remainingSecs = remainingSeconds % 60;

      // const timeStr = `${elapsedMinutes}:${String(elapsedSeconds).padStart(2, '0')} / ${remainingMinutes}:${String(remainingSecs).padStart(2, '0')}`;
      // console.log(`%c🔄 Token Refresh Time: ${timeStr}`, 'color: #FF9800; font-weight: bold;');
    }, 1000);
  }

  /**
   * Clears the token refresh countdown timer.
   */
  private clearRefreshCountdownTimer(): void {
    if (this.refreshCountdownTimer) {
      clearInterval(this.refreshCountdownTimer);
      this.refreshCountdownTimer = null;
    }
    this.elapsedRefreshSeconds = 0;
  }

    /**
   * Starts a timer to refresh token every 25 minutes.
   * Token is refreshed via POST call to /api/ext/users/refresh-user
   * This timer is independent of user activity and runs continuously.
   */
  private startTokenRefreshTimer(): void {
    this.clearTokenRefreshTimer();

    // Refresh token every 25 minutes (1500 seconds)
    const tokenRefreshIntervalMs = 25 * 60 * 1000;
    this.tokenRefreshTimer = setInterval(
      () => {
        // console.log('%c🔄 TOKEN REFRESH TIMER FIRED - Calling refreshToken()', 'color: #FF9800; font-weight: bold; font-size: 12px;');
        this.refreshToken();
      },
      tokenRefreshIntervalMs
    );

    // const startMsg =
    //   '%c🔄 TOKEN REFRESH TIMER STARTED - Token will refresh every 25 minutes (independent of user activity)';
    // const startStyle = 'color: #FF9800; font-weight: bold; font-size: 12px;';
    // console.log(startMsg, startStyle);
    this.logService.log(`Token refresh timer set to ${tokenRefreshIntervalMs / 1000 / 60} minutes`, 'info');

    // Start console countdown timer for token refresh
    this.startRefreshCountdownTimer();
  }

  /**
   * Clears the token refresh timer.
   */
  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
    this.clearRefreshCountdownTimer();
  }

  /**
   * Refreshes the user token by calling the refresh-user API endpoint.
   * Called every 30 minutes when user is active.
   */
  private refreshToken(): void {
    // const refreshMsg = '%c🔑 REFRESHING TOKEN - Calling /api/ext/users/refresh-user';
    // const refreshStyle = 'color: #FF9800; font-weight: bold;';
    // console.log(refreshMsg, refreshStyle);
    this.logService.log('Refreshing user token', 'info');

    // Make POST call to refresh token
    this.dataService.refreshUser().subscribe(
      () => {
        // const successMsg = '%c✅ TOKEN REFRESHED SUCCESSFULLY';
        // const successStyle = 'color: #4CAF50; font-weight: bold;';
        // console.log(successMsg, successStyle);
        this.logService.log('Token refreshed successfully', 'info');
      },
      error => {
        // const errorMsg = '%c❌ TOKEN REFRESH ERROR';
        // const errorStyle = 'color: #FF0000; font-weight: bold;';
        // console.log(errorMsg, errorStyle);
        this.logService.log(`Token refresh error: ${error.message}`, 'error');
        // log out the user on refresh failure and clear local and session storage
        localStorage.clear();
        sessionStorage.clear();
        this.store.dispatch(new AzureLogout({} as any));
        this.userContextService.logout();
      }
    );
  }

  /**
   *
   */
  getExternalApiData() {
    if (!Object(isDevMode)()) {
      this.store.dispatch(new InitSso());
      this.store
        .select(ApiEcoState.getAuthData)
        .pipe(
          filter((authData: AuthData) => Boolean(authData?.access_token)),
          take(1),
          takeUntil(this.destroyed)
        )
        .subscribe((authData: AuthData) => {
          this.authData = authData;
          this.store.dispatch(new GetExternalApiData());
        });
    } else {
      this.store.dispatch(new GetExternalApiData());
    }
  }

  /**
   *
   */
  ngOnDestroy(): void {
    this.titleService.setDocTitle('api-ecosystem');
    this.idleInitialized = false;
    this.clearIdleCountdownTimer();
    this.clearTokenRefreshTimer();
    this.destroyed.next('');
    this.destroyed.complete();
  }
}
